import { type NativeFormControlElement } from '@mui/base/FormControl/FormControl.types.js';
import clsx from 'clsx';
import parseMilliseconds from 'parse-ms';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';

import { IconButton } from '../Button/IconButton.tsx';
import type {
  ComponentProps,
  SlotComponentPropsWithoutOverride,
} from '../components.ts';
import { PauseIcon, PlayIcon, VolumeUpIcon } from '../icons/solid.tsx';
import { Slider, type SliderProps } from '../Slider/Slider.tsx';
import { assocDefaultStyle } from '../utils/assign-default-style.ts';
import { deepMerge } from '../utils/deep-merge.ts';
import { mergeRootSlotPropsToComponentProps } from '../utils/merge-root-slot-props-to-component-prop.ts';

interface SlotProps {
  currentDuration?: SlotComponentPropsWithoutOverride<'span'>;
  duration?: SlotComponentPropsWithoutOverride<'div'>;
  root?: SlotComponentPropsWithoutOverride<'div'>;
  toggle?: SlotComponentPropsWithoutOverride<'button'>;
  totalDuration?: SlotComponentPropsWithoutOverride<'span'>;
  volumeControl?: SlotComponentPropsWithoutOverride<'div'>;
  volumeSlider?: SliderProps;
}

export type AudioProps = ComponentProps<
  SlotProps,
  {
    src: string;
    type: string;
  }
>;

function formatSecond(second: number) {
  if (isNaN(second)) return '00:00';
  if (!Number.isFinite(second)) return '00:00';
  const { minutes, seconds } = parseMilliseconds(second * 1000);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`;
}

export function AudioPlayer({
  'data-testid': testId,
  disableDefaultClasses,
  slotProps: givenSlotProps,
  src,
  type,
  ...rest
}: AudioProps) {
  const [audioError, setAudioError] = useState<Error | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState('00:00');
  const [audioVolume, setAudioVolume] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const toggleAudioPlay = useCallback(async () => {
    if (!audioRef.current) return;
    try {
      if (audioPlaying) {
        await audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setAudioVolume((audioRef.current.volume ?? 0) * 100);
      setAudioPlaying(prevState => !prevState);
    } catch (e) {
      setAudioError(e as Error);
    }
  }, [audioPlaying]);
  const adjustAudioVolume = useCallback(
    (event: React.ChangeEvent<NativeFormControlElement>) => {
      if (!audioRef.current) return;
      const volume = Number.parseInt(event.target.value, 10);
      setAudioVolume(volume);
      audioRef.current.volume = volume / 100.0;
    },
    [],
  ) as any;
  useEffect(function subscribeAudioTimeChange() {
    const audioEle = audioRef.current;
    const updateAudioCurrentTime: (event: Event) => void = () =>
      setAudioDuration(formatSecond(audioRef.current?.currentTime ?? 0));
    audioEle?.addEventListener('timeupdate', updateAudioCurrentTime);
    return () =>
      audioEle?.removeEventListener('timeupdate', updateAudioCurrentTime);
  }, []);
  if (audioError)
    return (
      <>
        <div>Error: {audioError.message}</div>
      </>
    );
  let slotProps = givenSlotProps;

  if (!disableDefaultClasses) {
    slotProps = assocDefaultStyle<SlotProps>({
      slotWithDefaultClasses: {
        currentDuration: clsx('tw-font-bold'),
        root: clsx('tw-flex', 'tw-items-center', 'tw-gap-1'),
        volumeControl: clsx('tw-flex', 'tw-gap-1.5'),
      },
    })(givenSlotProps);
    slotProps.volumeSlider = deepMerge<SliderProps, SliderProps>(
      slotProps.volumeSlider ?? {},
      {},
    ) as any;
  }
  const rootProps = mergeRootSlotPropsToComponentProps()(slotProps, rest);
  return (
    <>
      <audio ref={audioRef}>
        <source src={src} type={type} />
      </audio>
      <div data-testid={testId && `${testId}-audio-controls`} {...rootProps}>
        <IconButton onClick={toggleAudioPlay}>
          {audioPlaying ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
        <div>
          <div className={slotProps?.duration?.className}>
            <span className={slotProps?.currentDuration?.className}>
              {audioDuration}
            </span>
            /
            <span className={slotProps?.totalDuration?.className}>
              {formatSecond(audioRef.current?.duration ?? 0)}
            </span>
          </div>
          <div className={slotProps?.volumeControl?.className}>
            <VolumeUpIcon />
            <div className={'tw-w-full'}>
              <Slider
                max={100}
                min={0}
                onChange={adjustAudioVolume}
                slotProps={slotProps?.volumeSlider?.slotProps}
                value={audioVolume}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
